import { Chip, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FoodsTags } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { ContractualAcceptance } from "src/api/client-api/consent/useGetContractualAcceptances";
import { getConsentCategoryConfig, hasKnownConsentCategory } from "./consentCategoryConfig";
import { ConsentPreviewButton } from "./ConsentPreviewButton";
import { getConsentPreviewText } from "./ConsentDocumentPreviewDialog";
import { formatConsentDateTime } from "./consentDateFormat";
import { getConsentDisplayTitle } from "./consentDisplay";

type ContractualAcceptanceRowProps = {
    acceptance: ContractualAcceptance;
    isLast: boolean;
    onViewDocument?: () => void;
    showCategoryBadge?: boolean;
};

export const ContractualAcceptanceRow = ({
    acceptance,
    isLast,
    onViewDocument,
    showCategoryBadge = true,
}: ContractualAcceptanceRowProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const locale = i18n?.resolvedLanguage || i18n?.language;
    const previewText = getConsentPreviewText(acceptance.summary, acceptance.content);
    const hasPreviewDocument = Boolean(onViewDocument);
    const hasCategory = Boolean(acceptance.category?.trim());
    const categoryConfig = hasCategory ? getConsentCategoryConfig(acceptance.category) : null;
    const categoryLabel =
        categoryConfig ?
            hasKnownConsentCategory(acceptance.category) ? t(categoryConfig.labelKey, categoryConfig.labelFallback)
            :   (acceptance.category?.trim() ?? null)
        :   null;
    const categoryColor =
        categoryConfig ?
            categoryConfig.colorKey === "grey" ?
                theme.custom.grey[2000]
            :   theme.palette[categoryConfig.colorKey].main
        :   undefined;
    const categoryTagSx = {
        height: 22,
        fontSize: "11px",
        fontWeight: 600,
        bgcolor: alpha(categoryColor ?? theme.custom.grey[2000], 0.08),
        color: categoryColor ?? theme.custom.grey[2000],
        borderRadius: "6px",
    };
    const displayTitle = getConsentDisplayTitle(acceptance.title, acceptance.version);

    return (
        <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{
                minHeight: "64px",
                px: { xs: theme.spacing(2), md: theme.spacing(4) },
                py: theme.spacing(1.5),
                gap: theme.spacing(1.5),
                borderBottom: isLast ? "none" : `1px solid ${theme.custom.grey[500]}`,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                width: "100%",
            }}
        >
            <Stack sx={{ flex: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden", width: "100%", gap: "2px" }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography
                        variant="body"
                        sx={{
                            color: theme.custom.grey[2800],
                            fontWeight: 700,
                            lineHeight: 1.5,
                            maxWidth: "100%",
                            overflowWrap: "anywhere",
                        }}
                    >
                        {displayTitle}
                    </Typography>
                    {showCategoryBadge && categoryLabel && (
                        <Chip label={categoryLabel} size="small" sx={categoryTagSx} />
                    )}
                    {acceptance.version && <FoodsTags label={`v${acceptance.version}`} variant="tags-grey" />}
                </Stack>
                <Typography variant="caption" sx={{ color: theme.custom.grey[2000], display: "block" }}>
                    {t("consent.acceptance.accepted_on", "Accepted on {{date}}", {
                        date: formatConsentDateTime(acceptance.acceptedAt, locale),
                    })}
                </Typography>
                {previewText && (
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            color: theme.custom.grey[2000],
                            lineHeight: 1.5,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            mt: "2px",
                            minWidth: 0,
                            width: "100%",
                        }}
                    >
                        {previewText}
                    </Typography>
                )}
            </Stack>
            <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                flexShrink={0}
                flexWrap="wrap"
                sx={{ width: { xs: "100%", md: "auto" } }}
            >
                <FoodsTags label={t("consent.acceptance.accepted", "Accepted")} variant="tags-green" />
                {hasPreviewDocument && <ConsentPreviewButton onClick={onViewDocument} />}
            </Stack>
        </Stack>
    );
};
